import axios from 'axios';
import camelCaseKeys from 'camelcase-keys';
import { AccountsService } from './services/AccountsService.js';
import { ActivityService } from './services/ActivityService.js';
import { AnalyticsService } from './services/AnalyticsService.js';
import { BundleMixesService } from './services/BundleMixesService.js';
import { BundleSelectionsService } from './services/BundleSelectionsService.js';
import { ClientAccountsService } from './services/ClientAccountsService.js';
import { EmissionEstimatesService } from './services/EmissionEstimatesService.js';
import { OffsetLinksService } from './services/OffsetLinksService.js';
import { OrdersService } from './services/OrdersService.js';
import { ProjectsService } from './services/ProjectsService.js';
import { WebhookRequestService } from './services/WebhookRequestService.js';
import { WebhooksService } from './services/WebhooksService.js';
function applyMixins(derivedCtor, constructors) {
    constructors.forEach((baseCtor) => {
        Object.getOwnPropertyNames(baseCtor.prototype).forEach((name) => {
            Object.defineProperty(derivedCtor.prototype, name, Object.getOwnPropertyDescriptor(baseCtor.prototype, name) || Object.create(null));
        });
    });
}
export class LuneClient {
    constructor(apiKey, baseUrl = 'https://api.lune.co', apiVersion = '1', account) {
        this.config = {
            BASE_URL: `${baseUrl}/v{api-version}`,
            VERSION: apiVersion,
            BEARER_TOKEN: apiKey,
            ACCOUNT: account,
        };
        this.client = axios.create();
        // Convert to camelCase when receiving request
        this.client.interceptors.response.use((response) => {
            return { ...response, data: camelCaseKeys(response.data, { deep: true }) };
        });
    }
    setAccount(accountId) {
        this.config.ACCOUNT = accountId;
    }
    /**
     * Perform an action with the current account being set to a desired one.
     *
     * The account will only be overridden for the duration of the action and will be restored
     * to the previous value at the end (when the result of withAccount() is awaited). The
     * account is also restored in case of an exception.
     *
     * This method modifies the internal state of LuneClient. Calling withAccount() or
     * setAccount() concurrently from multiple contexts at the same time is not supported and
     * will result in undefined behavior.
     *
     * The action's return value is returned by this function as-is.
     */
    async withAccount(accountId, action) {
        const previousAccountId = this.config.ACCOUNT;
        try {
            this.config.ACCOUNT = accountId;
            return await action();
        }
        finally {
            this.config.ACCOUNT = previousAccountId;
        }
    }
}
applyMixins(LuneClient, [
    AccountsService,
    ActivityService,
    AnalyticsService,
    BundleMixesService,
    BundleSelectionsService,
    ClientAccountsService,
    EmissionEstimatesService,
    OffsetLinksService,
    OrdersService,
    ProjectsService,
    WebhookRequestService,
    WebhooksService,
]);
export { Account } from './models/Account.js';
export { AccountScope } from './models/AccountScope.js';
export { AccountType } from './models/AccountType.js';
export { Activity } from './models/Activity.js';
export { Area } from './models/Area.js';
export { BundleSummary } from './models/BundleSummary.js';
export { CabinClass } from './models/CabinClass.js';
export { ClientAccount } from './models/ClientAccount.js';
export { CloudProvider } from './models/CloudProvider.js';
export { ContainerShippingMethod } from './models/ContainerShippingMethod.js';
export { Diet } from './models/Diet.js';
export { Distance } from './models/Distance.js';
export { ElectricityConsumption } from './models/ElectricityConsumption.js';
export { Error } from './models/Error.js';
export { HugeOilTankerSeaShippingMethod } from './models/HugeOilTankerSeaShippingMethod.js';
export { IndividualEstimateRequest } from './models/IndividualEstimateRequest.js';
export { MassUnit } from './models/MassUnit.js';
export { OffsetLinkStatus } from './models/OffsetLinkStatus.js';
export { OrderBase } from './models/OrderBase.js';
export { RoRoSeaShippingMethod } from './models/RoRoSeaShippingMethod.js';
export { Shipment } from './models/Shipment.js';
export { SimpleShippingMethod } from './models/SimpleShippingMethod.js';
export { VariableFuelSeaShippingMethod } from './models/VariableFuelSeaShippingMethod.js';
export { VariableFuelVariableLoadSeaShippingMethod } from './models/VariableFuelVariableLoadSeaShippingMethod.js';
export { WebhookEvent } from './models/WebhookEvent.js';
export { AccountsService } from './services/AccountsService.js';
export { ActivityService } from './services/ActivityService.js';
export { AnalyticsService } from './services/AnalyticsService.js';
export { BundleMixesService } from './services/BundleMixesService.js';
export { BundleSelectionsService } from './services/BundleSelectionsService.js';
export { ClientAccountsService } from './services/ClientAccountsService.js';
export { EmissionEstimatesService } from './services/EmissionEstimatesService.js';
export { OffsetLinksService } from './services/OffsetLinksService.js';
export { OrdersService } from './services/OrdersService.js';
export { ProjectsService } from './services/ProjectsService.js';
export { WebhookRequestService } from './services/WebhookRequestService.js';
export { WebhooksService } from './services/WebhooksService.js';
//# sourceMappingURL=client.js.map